import React, { useEffect, useState } from "react";
import { Box, Button, Stack, useMediaQuery, useTheme } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  getReportDownloadLabel,
  reportDownloadDesktopSx,
} from "screens/Reports/helpers/reportsToolbarStyles";
import ReportsSearchField from "screens/Reports/components/ReportsSearchField";
import ReportsRecordList, {
  ReportRecord,
} from "screens/Reports/components/ReportsRecordList";
import reportsTokens from "screens/Reports/Reports.tokens";
import CustomTable from "global/components/CustomTable/CustomTable";
import {
  reportsLoadingRowHeight,
  reportsTableCardSx,
  reportsTableIdentitySx,
  reportsTableNameSx,
  reportsTableSubSx,
  reportsTableSx,
} from "screens/Reports/ReportsTable.styles";
import strings from "global/constants/StringConstants";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import { handleErrorMessage } from "helpers/methods";
import { SortingConfig } from "models/interfaces";
import {
  getUserRequestAnalyticsCount,
  getUserRequestAnalyticsPage,
} from "../services/ReportsService";
import moment from "moment";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";

type UserRow = {
  rowId: string;
  user: JSX.Element;
  /** Names this row's selection checkbox for screen readers (WCAG 4.1.2). */
  selectionLabel: string;
  nameText: string;
  emailText: string;
  envelopesSent: number;
  envelopesCompleted: number;
  templatesCreated: number;
};

const HEADERS = [
  { name: "User", field: "user", align: "left", sorting: false },
  {
    name: "Requests Sent",
    field: "envelopesSent",
    align: "right",
    sorting: false,
  },
  {
    name: "Requests Completed",
    field: "envelopesCompleted",
    align: "right",
    sorting: false,
  },
  {
    name: "Templates Created",
    field: "templatesCreated",
    align: "right",
    sorting: false,
  },
];
const DEFAULT_SORTING_CONFIG: SortingConfig[] = [
  { entity: "signRequest", field: "createdOn", order: "DESC" },
];
const toNumber = (val: any) => {
  if (typeof val === "number") return Number.isFinite(val) ? val : 0;
  if (typeof val === "string") {
    const parsed = Number(val.replace(/,/g, "").trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getAny = (obj: any, keys: string[], fallback: any = "") => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== "") {
      return obj[key];
    }
  }
  return fallback;
};

const UserRequestAnalyticsReport = ({
  setLoader,
}: {
  setLoader?: (value: boolean) => void;
}) => {
  const theme = useTheme();
  /**
   * `Reports - Mobile & Tablet.dc.html` has no table below `md` — the rows
   * become cards on a phone and a bordered list on the tablet. The two are
   * swapped rather than both rendered, so only one selection UI is ever in the
   * accessibility tree.
   */
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const usageReportsState = useFeatureState(FeatureKeys.TEAM_USAGE_REPORTS);
  const isUsageReportsAvailable =
    usageReportsState.status === strings.FEATURE_STATUS_AVAILABLE;

  const [rows, setRows] = useState<UserRow[]>([]);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState({
    page: 1,
    rowsPerPage: 10,
    searchText: "",
  });
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedRowsMap, setSelectedRowsMap] = useState<
    Record<string, UserRow>
  >({});
  const sortingConfig = DEFAULT_SORTING_CONFIG;

  const convertTableData = (data: any[]): UserRow[] => {
    return data.map((item: any, index: number) => {
      const name = getAny(item, ["name", "userName", "requestedByName"], "-");
      const email = getAny(
        item,
        ["email", "userEmail", "requestedByEmail"],
        "-",
      );
      const envelopesSent = toNumber(
        getAny(item, ["envelopes_sent", "envelopesSent", "sent"], 0),
      );
      const envelopesCompleted = toNumber(
        getAny(
          item,
          ["envelopes_completed", "envelopesCompleted", "completed"],
          0,
        ),
      );
      const templatesCreated = toNumber(
        getAny(item, ["templates_created", "templatesCreated"], 0),
      );

      return {
        rowId: `${String(
          getAny(item, ["id", "userId", "_id"], name),
        )}-${index}`,
        user: (
          <Box sx={reportsTableIdentitySx}>
            <Box sx={reportsTableNameSx} title={String(name)}>
              {name}
            </Box>
            <Box sx={reportsTableSubSx} title={String(email)}>
              {email}
            </Box>
          </Box>
        ),
        selectionLabel: `${String(name)}, ${String(email)}`,
        nameText: String(name),
        emailText: String(email),
        envelopesSent,
        envelopesCompleted,
        templatesCreated,
      };
    });
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setLoader?.(true);
      const payload = {
        rowsPerPage: filters.rowsPerPage,
        searchText: filters.searchText,
        sortingConfig,
      };

      const countPayload = {
        searchText: filters.searchText,
      };

      const [dataResponse, countResponse] = await Promise.all([
        getUserRequestAnalyticsPage(filters.page, payload),
        getUserRequestAnalyticsCount(countPayload),
      ]);

      const rawRows = Array.isArray(dataResponse)
        ? dataResponse
        : Array.isArray(dataResponse?.data)
          ? dataResponse.data
          : Array.isArray(dataResponse?.content)
            ? dataResponse.content
            : [];

      setRows(convertTableData(rawRows));
      setCount(toNumber(countResponse?.count ?? countResponse ?? 0));
    } catch (error: any) {
      handleErrorMessage(error);
      setRows([]);
      setCount(0);
    } finally {
      setIsLoading(false);
      setLoader?.(false);
    }
  };

  useEffect(() => {
    if (!isUsageReportsAvailable) {
      setIsLoading(false);
      setLoader?.(false);
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.page,
    filters.rowsPerPage,
    filters.searchText,
    isUsageReportsAvailable,
  ]);

  useEffect(() => {
    setSelected([]);
    setSelectedRowsMap({});
  }, [filters.searchText]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    setFilters((prev) => ({ ...prev, page: 1, searchText }));
  };

  const handlePageChange = (_: any, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (rowsPerPage: number) => {
    setFilters((prev) => ({ ...prev, page: 1, rowsPerPage }));
  };

  const handleSelectOne = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
    const foundRow = rows.find((r) => r.rowId === id);
    if (foundRow) {
      setSelectedRowsMap((prev) => ({ ...prev, [id]: foundRow }));
    }
  };

  const handleSelectAll = () => {
    const visibleRowIds = rows.map((row) => row.rowId);
    setSelected((prev) => {
      const next = [...prev];
      visibleRowIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return next;
    });
    setSelectedRowsMap((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        next[row.rowId] = row;
      });
      return next;
    });
  };

  const handleDeselectOne = (id: string) => {
    setSelected((prev) => prev.filter((item) => item !== id));
    setSelectedRowsMap((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const handleDeselectAll = () => {
    const visibleRowIds = rows.map((row) => row.rowId);
    setSelected((prev) => prev.filter((id) => !visibleRowIds.includes(id)));
    setSelectedRowsMap((prev) => {
      const next = { ...prev };
      visibleRowIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
  };

  const handleDownloadCSV = () => {
    const selectedData = selected
      .map((id) => selectedRowsMap[id])
      .filter(Boolean);

    const headers = [
      "Name",
      "Email",
      "Requests Sent",
      "Requests Completed",
      "Templates Created",
    ];

    const csvRows = selectedData.map((row) => [
      `"${row.nameText.replace(/"/g, '""')}"`,
      `"${row.emailText.replace(/"/g, '""')}"`,
      row.envelopesSent,
      row.envelopesCompleted,
      row.templatesCreated,
    ]);

    const csvContent = [headers, ...csvRows].map((e) => e.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `User_Analytics${moment().format("YYYY-MM-DD")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const records: ReportRecord[] = rows.map((row) => ({
    rowId: row.rowId,
    name: row.nameText,
    sub: row.emailText === "-" ? undefined : row.emailText,
    selectionLabel: row.selectionLabel,
    pairs: [
      { label: "Sent", value: String(row.envelopesSent) },
      { label: "Completed", value: String(row.envelopesCompleted) },
      { label: "Templates", value: String(row.templatesCreated) },
    ],
  }));

  if (!isUsageReportsAvailable) {
    return (
      <FeatureGateBlock
        variant="full"
        featureName="Usage reports"
        status={usageReportsState.status as "not-in-plan" | "admin-disabled"}
        disabledMessage={(usageReportsState as any).disabledMessage}
      />
    );
  }

  return (
    <Stack
      spacing={{ xs: 1.75, sm: 2, md: reportsTokens.toolbar.marginBottom }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, md: reportsTokens.toolbar.gap }}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <ReportsSearchField
          placeholder="Search users"
          onSearchChange={handleSearchChange}
        />

        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleDownloadCSV}
          disabled={selected.length === 0}
          sx={reportDownloadDesktopSx}
        >
          {getReportDownloadLabel(selected.length)}
        </Button>
      </Stack>
      {isCompact ? (
        <ReportsRecordList
          records={records}
          selected={selected}
          onSelectOne={handleSelectOne}
          onDeselectOne={handleDeselectOne}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          isLoading={isLoading}
          onDownload={handleDownloadCSV}
          emptyTitle="No user activity yet"
          emptySub="Once your team starts sending sign requests, per-user activity will show up here."
          ariaLabel="User analytics"
          page={filters.page}
          rowsPerPage={filters.rowsPerPage}
          count={count}
          onPageChange={(nextPage) => handlePageChange(null, nextPage)}
        />
      ) : (
        <CustomTable
          emptyState={{
            icon: "report",
            title: "No user activity yet",
            body: "Once your team starts sending requests, each member's totals appear here.",
          }}
          loadingRowHeight={reportsLoadingRowHeight}
          cardSx={reportsTableCardSx}
          tableSxOverride={reportsTableSx}
          ariaLabel="User analytics"
          headers={HEADERS}
          rows={rows}
          count={count}
          page={filters.page}
          rowsPerPage={filters.rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
          showJumpToPageNumber={false}
          handleOnChangePageInput={handlePageChange}
          showPagination
          isLoading={isLoading}
          showCheckbox={true}
          hideSelectedCount
          highlightSelectedRows
          selected={selected}
          onSelectOne={handleSelectOne}
          onSelectAll={handleSelectAll}
          onDeselectOne={handleDeselectOne}
          onDeselectAll={handleDeselectAll}
        />
      )}
    </Stack>
  );
};

export default UserRequestAnalyticsReport;
